import React, { useState, useEffect } from 'react';
import { Plus, Pencil, X, Ticket } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const EMPTY = {
  code: '', type: 'percent', value: 10, startDate: '', endDate: '',
  maxUses: null, maxUsesPerCustomer: 1, active: true, ownerId: 'matriz', notes: '',
};

export default function AdminCupones() {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [franchises, setFranchises] = useState([]);

  useEffect(() => {
    api.getCoupons().then(setCoupons).catch(() => {});
    api.getFranchisesFresh().then(setFranchises).catch(() => {});
  }, []);

  const toggleActive = async (c) => {
    try {
      const updated = await api.updateCoupon(c.id, { active: !c.active });
      setCoupons((prev) => prev.map((x) => x.id === c.id ? updated : x));
      toast.success(`Cupón ${c.code} ${updated.active ? 'activado' : 'desactivado'}`);
    } catch { toast.error('Error al actualizar'); }
  };

  const save = async () => {
    if (!editing.code.trim() || !editing.value) { toast.error('Código y valor son obligatorios'); return; }
    try {
      if (editing.id) {
        const { id, uses, ...data } = editing;
        const updated = await api.updateCoupon(id, data);
        setCoupons((prev) => prev.map((c) => c.id === id ? updated : c));
      } else {
        const created = await api.createCoupon(editing);
        setCoupons((prev) => [created, ...prev]);
      }
      setEditing(null);
      toast.success('Cupón guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-cupones">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Ticket className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-black text-stone-800">Cupones</h1>
          </div>
          <p className="text-sm text-stone-500">Códigos promocionales para campañas. Validación automática en el checkout.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-coupon-button">
          <Plus className="w-4 h-4" /> Nuevo cupón
        </button>
      </div>

      {editing && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-8" data-testid="coupon-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-stone-800">{editing.id ? `Editar ${editing.code}` : 'Nuevo cupón'}</h2>
            <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Código *</label>
              <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} placeholder="VERANO25" className="w-full border border-stone-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="coupon-code-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Tipo de descuento</label>
              <select value={editing.type} onChange={set('type')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="coupon-type-select">
                <option value="percent">Porcentaje (%)</option>
                <option value="fixed">Importe fijo (€)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Valor * {editing.type === 'percent' ? '(%)' : '(€)'}</label>
              <input type="number" step="0.01" value={editing.value} onChange={(e) => setEditing({ ...editing, value: parseFloat(e.target.value) || 0 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="coupon-value-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Fecha inicio</label>
              <input type="date" value={editing.startDate} onChange={set('startDate')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Fecha fin</label>
              <input type="date" value={editing.endDate} onChange={set('endDate')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-stone-500 mb-1">Límite usos</label>
                <input type="number" value={editing.maxUses ?? ''} onChange={(e) => setEditing({ ...editing, maxUses: e.target.value ? parseInt(e.target.value) : null })} placeholder="∞" className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 mb-1">Por cliente</label>
                <input type="number" value={editing.maxUsesPerCustomer ?? ''} onChange={(e) => setEditing({ ...editing, maxUsesPerCustomer: e.target.value ? parseInt(e.target.value) : null })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Franquicia propietaria</label>
              <select value={editing.ownerId} onChange={set('ownerId')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 mb-1">Observaciones internas</label>
              <input value={editing.notes} onChange={set('notes')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={save} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-coupon-button">Guardar cupón</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Código</th>
              <th className="px-4 py-3 font-medium">Descuento</th>
              <th className="px-4 py-3 font-medium">Vigencia</th>
              <th className="px-4 py-3 font-medium">Usos</th>
              <th className="px-4 py-3 font-medium">Propietario</th>
              <th className="px-4 py-3 font-medium w-20">Activo</th>
              <th className="px-4 py-3 w-14"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-stone-100" data-testid={`coupon-row-${c.code}`}>
                <td className="px-4 py-3">
                  <span className="font-mono font-bold text-stone-800 bg-stone-100 px-2 py-1 rounded">{c.code}</span>
                  {c.notes && <p className="text-[11px] text-stone-400 mt-1">{c.notes}</p>}
                </td>
                <td className="px-4 py-3 font-bold text-teal-600">{c.type === 'percent' ? `${c.value}%` : `${c.value.toFixed(2).replace('.', ',')} €`}</td>
                <td className="px-4 py-3 text-stone-500 text-xs">{c.startDate || '—'} → {c.endDate || '—'}</td>
                <td className="px-4 py-3 text-stone-600">{c.uses || 0}{c.maxUses ? ` / ${c.maxUses}` : ''}</td>
                <td className="px-4 py-3 text-stone-600 text-xs">{franchises.find((f) => f.id === c.ownerId)?.name || '—'}</td>
                <td className="px-4 py-3"><Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`toggle-coupon-${c.code}`} /></td>
                <td className="px-4 py-3">
                  <button onClick={() => setEditing({ ...c })} className="text-stone-400 hover:text-amber-600 transition-colors" data-testid={`edit-coupon-${c.code}`}><Pencil className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {coupons.length === 0 && <p className="text-center text-stone-400 py-10">No hay cupones todavía.</p>}
      </div>
    </div>
  );
}
